import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Input, Modal, Form,
  Typography, Space, Popconfirm, Alert, Empty, Tag, Drawer, Avatar, theme as antdTheme,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, UnorderedListOutlined, SearchOutlined,
  TeamOutlined, UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const Lists = () => {
  const { token: themeToken } = antdTheme.useToken();
  const [lists,        setLists]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [modalOpen,    setModalOpen]    = useState(false);
  const [creating,     setCreating]     = useState(false);
  const [search,       setSearch]       = useState('');

  // Subscriber drawer
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [drawerList,   setDrawerList]   = useState(null);   // { id, name }
  const [subSearch,    setSubSearch]    = useState('');
  const [subscribers,  setSubscribers]  = useState([]);
  const [subTotal,     setSubTotal]     = useState(0);
  const [subLoading,   setSubLoading]   = useState(false);

  const [form] = Form.useForm();

  const fetchLists = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/lists');
      setLists(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load lists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLists(); }, []);

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      await axios.post('/api/lists', values);
      form.resetFields();
      setModalOpen(false);
      fetchLists();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create list');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/lists/${id}`);
      fetchLists();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete list');
    }
  };

  const openSubscribers = async (list) => {
    setDrawerList(list);
    setSubSearch('');
    setSubscribers([]);
    setSubTotal(0);
    setDrawerOpen(true);
    setSubLoading(true);
    try {
      // Fetch all subscribers in one go (cap at 5000 to stay within reason)
      const res = await axios.get(`/api/subscribers?list_id=${list.id}&limit=5000`);
      const data = res.data.data ?? res.data;
      setSubscribers(data);
      setSubTotal(res.data.total ?? data.length);
    } catch {
      setSubscribers([]);
    } finally {
      setSubLoading(false);
    }
  };

  const filtered    = lists.filter(l => l.name.toLowerCase().includes(search.toLowerCase()));
  const filteredSubs = subscribers.filter(s =>
    s.name?.toLowerCase().includes(subSearch.toLowerCase()) ||
    s.email?.toLowerCase().includes(subSearch.toLowerCase())
  );

  const columns = [
    {
      title: 'List Name', dataIndex: 'name',
      render: name => (
        <Space size={10}>
          <div style={{ width: 34, height: 34, background: '#ede9fe', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <UnorderedListOutlined style={{ color: '#6366f1', fontSize: 15 }} />
          </div>
          <Text strong style={{ fontSize: 14 }}>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Subscribers', dataIndex: 'subscriber_count', width: 130,
      render: (n, record) => (
        <Tag
          color="processing"
          icon={<TeamOutlined />}
          style={{ fontWeight: 600, borderRadius: 6, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => openSubscribers(record)}
        >
          {n ?? 0} subscriber{(n ?? 0) !== 1 ? 's' : ''}
        </Tag>
      ),
    },
    {
      title: 'Created', dataIndex: 'created_at', width: 160,
      render: d => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
    },
    {
      title: '', align: 'right', width: 100,
      render: (_, record) => (
        <Popconfirm
          title="Delete this list?"
          description="All subscriber mappings for this list will be removed."
          onConfirm={() => handleDelete(record.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <Button danger icon={<DeleteOutlined />} size="small">Delete</Button>
        </Popconfirm>
      ),
    },
  ];

  const subColumns = [
    {
      title: 'Subscriber', key: 'name',
      render: (_, r) => {
        const displayName = [r.first_name, r.last_name].filter(Boolean).join(' ') || r.name || '';
        return (
          <Space size={10}>
            <Avatar
              size={34}
              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', fontSize: 13, fontWeight: 700, flexShrink: 0 }}
            >
              {displayName ? displayName.charAt(0).toUpperCase() : <UserOutlined />}
            </Avatar>
            <div>
              <Text strong style={{ fontSize: 13, display: 'block' }}>{displayName || '—'}</Text>
              <Text style={{ fontSize: 12, color: themeToken.colorTextSecondary }}>{r.email}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Location', key: 'location', width: 140,
      render: (_, r) => {
        const loc = [r.city, r.state, r.country].filter(Boolean).join(', ');
        return <Text style={{ fontSize: 12, color: themeToken.colorTextSecondary }}>{loc || '—'}</Text>;
      },
    },
    {
      title: 'Added', dataIndex: 'created_at', width: 110,
      render: d => <Text style={{ fontSize: 12, color: themeToken.colorTextSecondary }}>{new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Mailing Lists</Title>
          <Text style={{ color: themeToken.colorTextSecondary }}>{lists.length} list{lists.length !== 1 ? 's' : ''} total</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setModalOpen(true)} style={{ borderRadius: 10, fontWeight: 600 }}>
          New List
        </Button>
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 10 }} />}

      <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search lists…"
            prefix={<SearchOutlined style={{ color: themeToken.colorTextTertiary }} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', maxWidth: 280, borderRadius: 10 }}
            allowClear
          />
        </div>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No lists yet. Create your first list!" /> }}
          pagination={{ pageSize: 10, showSizeChanger: false, showTotal: t => `${t} list${t !== 1 ? 's' : ''}` }}
          scroll={{ x: 400 }}
        />
      </Card>

      {/* ── Create List Modal ── */}
      <Modal
        title={
          <Space>
            <div style={{ width: 32, height: 32, background: '#ede9fe', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UnorderedListOutlined style={{ color: '#6366f1' }} />
            </div>
            <span>Create Mailing List</span>
          </Space>
        }
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnHidden
        width={440}
      >
        <Form form={form} onFinish={handleCreate} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="name" label="List Name" rules={[{ required: true, message: 'Please enter a list name' }]}>
            <Input placeholder="e.g., Monthly Newsletter" size="large" style={{ borderRadius: 10 }} autoFocus />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={creating} style={{ borderRadius: 8 }}>
                Create List
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Subscribers Drawer ── */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={560}
        destroyOnHidden
        title={
          <Space size={10}>
            <div style={{ width: 32, height: 32, background: '#ede9fe', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TeamOutlined style={{ color: '#6366f1' }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>{drawerList?.name}</div>
              <div style={{ fontWeight: 400, fontSize: 12, color: themeToken.colorTextSecondary }}>
                {subLoading ? 'Loading…' : `${subTotal} subscriber${subTotal !== 1 ? 's' : ''}`}
              </div>
            </div>
          </Space>
        }
      >
        <Input
          placeholder="Search by name or email…"
          prefix={<SearchOutlined style={{ color: themeToken.colorTextTertiary }} />}
          value={subSearch}
          onChange={e => setSubSearch(e.target.value)}
          allowClear
          style={{ borderRadius: 10, marginBottom: 16 }}
        />
        <Table
          dataSource={filteredSubs}
          columns={subColumns}
          rowKey="id"
          loading={subLoading}
          size="small"
          pagination={{ pageSize: 10, showTotal: t => `${t} subscriber${t !== 1 ? 's' : ''}`, showSizeChanger: false }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No subscribers in this list" /> }}
        />
      </Drawer>
    </div>
  );
};

export default Lists;
